// @ts-strict-ignore
import { useMemo, useCallback } from 'react'
import pick from 'lodash/pick'

import { AppointmentBookingProps } from 'APP/Components/AppointmentBooking'
import {
  Answer,
  MattermostQuestion,
  SilkroadSelfSchedulingQuestion,
  BookingProps,
  AnswerMessage,
} from './types'
import { isMatterMostPost, convertAnswerToSilkroadFormat } from './messageHelpers'
import { BOOKING_FLOW } from '../constants'
import type { AppointmentBookingFields } from '../AppointmentBookingBase/types'

export function renameKeys(
  /* eslint-disable-next-line */
  obj: Record<string, any>,
  keysToRename: Record<string, string>
  /* eslint-disable-next-line */
): Record<string, any> {
  const objWithRenames = Object.assign({}, obj)
  Object.entries(keysToRename).forEach(([key, newKey]) => {
    if (objWithRenames[key]) {
      objWithRenames[newKey] = objWithRenames[key]
      delete objWithRenames[key]
    }
  })

  return objWithRenames
}

// -------------------------------------- //
// COPY-PASTED AS-IS FROM THE MICROAPP.
// IF THE CODE IS WEIRD HERE, WHO KNOWS WHY
// -------------------------------------- //
export function useCompatibility(
  { post, sendAnswer: sendAnswerProp, ...rest }: AppointmentBookingProps,
  flow: BOOKING_FLOW
): AppointmentBookingFields & {
  onComplete: (answer: Answer) => void
} {
  const appPropsKeys = [
    'date_from',
    'date_until',
    'appointment_type',
    'requested_slot',
    'is_rescheduling',
    'appointment_id',
    'booking_id',
  ]
  const appPropsFromProps = pick(rest, appPropsKeys)
  const isMMPost = post && isMatterMostPost(post)
  const isSilkroadPost = post && !isMMPost
  const appProps = useMemo(() => {
    /* eslint-disable-next-line */
    let nextAppProps = appPropsFromProps as any

    if (isMMPost) {
      const mmPost = post as MattermostQuestion
      // Handle both new flat structure and legacy microapp structure
      const sourceProps = mmPost.props.microapp || mmPost.props
      nextAppProps = pick(sourceProps, appPropsKeys)
    } else if (isSilkroadPost) {
      nextAppProps = pick((post as SilkroadSelfSchedulingQuestion).microapp_props, appPropsKeys)
    }

    if (post?.props?.payload?.slot_to_set) {
      nextAppProps.requested_slot = post?.props?.payload.slot_to_set
    }

    // Handle legacy microapp requested_slot if not already set
    if (!nextAppProps.requested_slot && post?.props?.microapp?.requested_slot) {
      nextAppProps.requested_slot = post.props.microapp.requested_slot
    }

    return nextAppProps as BookingProps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...Object.values(appPropsFromProps), post, isMMPost, isSilkroadPost])

  const onComplete = useCallback(
    (answer: Answer) => {
      let compatibleAnswer: Answer | AnswerMessage = answer
      const answerPayload = renameKeys(answer.payload, {
        service_id: 'ih_service_id',
        // TODO: stop sending ih_practitioner_user_id, BM already supports provider_id directly
        // https://github.com/dialoguemd/beautiful-mind/blob/d2a8bc675b9205e9468b586d8150c40f4c74e279/action-server/beautiful_mind/utils.py#L57-L71
        ...(flow === BOOKING_FLOW.TIME_BASED && { provider_id: 'ih_practitioner_user_id' }),
      })

      if (isMMPost || isSilkroadPost) {
        const payload = `/answer_question${JSON.stringify({
          [appProps.requested_slot]: answer.payload.is_booked ? answerPayload : {},
        })}`

        if (isMMPost) {
          compatibleAnswer = {
            payload: [payload],
            type: 'answer',
            display: answer.display,
            question_type: 'multiChoiceQuestion',
            answer_flags: { update_channel_state: false },
          }
        }
        if (isSilkroadPost) {
          compatibleAnswer = convertAnswerToSilkroadFormat(
            post as SilkroadSelfSchedulingQuestion,
            payload,
            answer.display
          )
        }
      }

      sendAnswerProp(compatibleAnswer)
    },
    [isMMPost, isSilkroadPost, appProps.requested_slot, post, sendAnswerProp, flow]
  )

  return {
    ...rest,
    ...appProps,
    onComplete,
  }
}
